/* eslint-disable */
import React from "react";
import editLight from "assets/images/manageTeam/editLight.svg";
import editDark from "assets/images/manageTeam/editDark.svg";
import deleteLight from "assets/images/manageTeam/deleteLight.svg";
import deleteDark from "assets/images/manageTeam/deleteDark.svg";
import ForgetPasswordLight from "assets/images/manageTeam/ForgetPasswordLight.svg";
import ForgetPasswordDark from "assets/images/manageTeam/ForgetPasswordDark.svg";
import showDark from "assets/images/manageTeam/show.svg";
import showLight from "assets/images/manageTeam/showdark.svg";

import "./manageUserTable.scss";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";
import Tooltip from "components/Tooltip/index";
import { ManageUser as MU, Organization } from "@induz/sdk";
import { useToast } from "components/Toaster/index";
import { useState, useEffect } from "react";
import DeleteModal from "components/DeleteModal/index";
import { GetActionIcon } from "constants/Homepage/ysafe/iconFunctions/getActionIcon";
import AuthenticationOtp from "components/AuthenticationModal/index";

function ManageUserTable({ roles, handleEditRole, refreshUsers }) {
  const theme = useTheme();
  const isMobile = useScreenMobile({ size: 992 });
  const isMobileStrict = useScreenMobile({ size: 768 });
  const [activeMenu, setActiveMenu] = React.useState(null);
  const showToast = useToast();
  const maxVisibleItems = 2;
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [isBlocking, setIsBlocking] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [showDeleteOtpModal, setShowDeleteOtpModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isUserBlocked = (role) => {
    if (optimisticStatus[role.email] !== undefined) {
      return optimisticStatus[role.email];
    }
    return !!role.isBlocked;
  };

  const editIcon = theme === "light" ? editLight : editDark;
  const forgetPassIcon =
    theme === "light" ? ForgetPasswordLight : ForgetPasswordDark;
  const deleteIcon = theme === "light" ? deleteLight : deleteDark;
  const showIcon = theme === "light" ? showLight : showDark;

  const [canBlockUnblock, setCanBlockUnblock] = useState(false);

  useEffect(() => {
    const checkPerms = async () => {
      try {
        const mail = sessionStorage.getItem("mail");
        if (!mail) return;
        const user = await MU.getUserDetails(mail);
        const isOrgAdmin = user instanceof Organization;
        const perms = typeof user.getOperations === "function" ? user.getOperations() || [] : [];
        // Operation 126 is BlockUser, 127 is UnblockUser
        setCanBlockUnblock(isOrgAdmin || perms.includes(126) || perms.includes(127));
      } catch (error) {
        console.error("Error checking permissions:", error);
      }
    };
    checkPerms();
  }, []);



  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeMenu !== null &&
        !event.target.closest(".mobile-action-menu-container")
      ) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenu]);

  // Function to truncate roles and teams
  function truncateItems(items, maxVisible) {
    const visible = items.slice(0, maxVisible);
    const remainingData = items.slice(maxVisible);
    const remaining = remainingData.length;
    return { visible, remaining, remainingData };
  }

  const handleChangePassword = async (index) => {
    const email = roles[index]?.email;
    try {
      await MU.changePasswordForOrgUser(email);
      showToast("Password change initiated successfully.", "success");
    } catch (error) {
      console.error("Error changing password:", error);
      showToast("Failed to change password. Please try again.", "error");
    }
  };
  const initiateUserDelete = (role) => {
    setUserToDelete(role);
    setShowDeleteOtpModal(true);
  };

  const handleUserDelete = async (verifiedOtp) => {
    const email = userToDelete?.email;
    if (!email) return;
    try {
      setIsDeleting(true);
      setShowDeleteOtpModal(false);
      await MU.removeOU(email, verifiedOtp);
      showToast("User removed successfully", "success");
      if (refreshUsers) {
        await refreshUsers();
      }
    } catch (error) {
      console.error("Error Removing user:", error);
      showToast("Failed to remove user. Please try again.", "error");
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  const handleUserBlock = async () => {
    if (!userToBlock) return;
    const email = userToBlock.email;
    // Optimistic UI update
    setShowBlockConfirm(false);
    setUserToBlock(null);
    setOptimisticStatus(prev => ({ ...prev, [email]: true }));
    try {
      console.log("handleUserBlock: attempting block for", email);
      setIsBlocking(true);
      const orgObj = new Organization();
      await orgObj.blockUser(email);
      console.log("handleUserBlock: block succeeded for", email);
      showToast("User blocked successfully", "success");
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      console.error("Error blocking user:", error);
      // Revert optimistic update
      setOptimisticStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[email];
        return newStatus;
      });
      showToast(error.message || "Failed to block user. Please try again.", "error");
    } finally {
      setIsBlocking(false);
    }
  };

  const initiateBlock = (role) => {
    setUserToBlock(role);
    setShowBlockConfirm(true);
  };

  const initiateUnblock = (role) => {
    setUserToUnblock(role);
    setShowUnblockConfirm(true);
  };

  const handleUserUnblock = async () => {
    if (!userToUnblock) return;
    const email = userToUnblock.email;
    // Optimistic UI update
    setShowUnblockConfirm(false);
    setUserToUnblock(null);
    setOptimisticStatus(prev => ({ ...prev, [email]: false }));
    try {
      console.log("handleUserUnblock: attempting unblock for", email);
      setIsUnblocking(true);
      const orgObj = new Organization();
      await orgObj.unblockUser(email);
      console.log("handleUserUnblock: unblock succeeded for", email);
      showToast("User unblocked successfully", "success");
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      console.error("Error unblocking user:", error);
      // Revert optimistic update
      setOptimisticStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[email];
        return newStatus;
      });
      showToast(error.message || "Failed to unblock user. Please try again.", "error");
    } finally {
      setIsUnblocking(false);
    }
  };

  return (
    <div className="manageUserTableViewport">
      {isMobileStrict ? (
        <table className="ManageUserTableMobile">
          <thead>
            <tr>
              <th className="col-user">Email</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role, index) => (
              <tr key={index}>
                <td className="col-user UserEmail">
                  <p>{role.email}</p>
                </td>
                <td className="col-actions Actions">
                  <div className="mobile-action-menu-container">
                    <button
                      className="three-dot-btn"
                      onClick={() =>
                        setActiveMenu(activeMenu === index ? null : index)
                      }
                    >
                      ⋮
                    </button>
                    {activeMenu === index && (
                      <div
                        className={`mobile-action-menu ${index === roles.length - 1 && roles.length > 1
                          ? "open-upward"
                          : ""
                          }`}
                      >
                        <div className="menu-item operations-label">
                          <p className="label-title">
                            Roles ({role.role.length}), Teams (
                            {role.team.length})
                          </p>
                          {role.role.length > 0 && (
                            <p className="operations-list-text">
                              Roles: {role.role.join(", ")}
                            </p>
                          )}
                          {role.team.length > 0 && (
                            <p className="operations-list-text">
                              Teams: {role.team.join(", ")}
                            </p>
                          )}
                        </div>
                        <div
                          className="menu-item"
                          onClick={() => {
                            handleEditRole(role, index);
                            setActiveMenu(null);
                          }}
                        >
                          <img src={showIcon} alt="view" />
                          Roles
                        </div>
                        <div
                          className="menu-item"
                          onClick={() => {
                            handleEditRole(role, index);
                            setActiveMenu(null);
                          }}
                        >
                          <img src={editIcon} alt="edit" />
                          Edit
                        </div>
                        <div
                          className="menu-item"
                          onClick={() => {
                            handleChangePassword(index);
                            setActiveMenu(null);
                          }}
                        >
                          <img src={forgetPassIcon} alt="password" />
                          Password
                        </div>
                        <div
                          className="menu-item"
                          onClick={() => {
                            initiateUserDelete(role);
                            setActiveMenu(null);
                          }}
                        >
                          <img src={deleteIcon} alt="delete" />
                          Delete
                        </div>
                        {canBlockUnblock && (
                          <div
                            className="menu-item"
                            onClick={() => {
                              isUserBlocked(role)
                                ? initiateUnblock(role)
                                : initiateBlock(role);
                              setActiveMenu(null);
                            }}
                          >
                            <GetActionIcon
                              type="block"
                              color={isUserBlocked(role) ? "#DC1818" : (theme === "light" ? "#211D25" : "#FFF")}
                              size={18}
                            />
                            {isUserBlocked(role) ? "Unblock" : "Block"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : isMobile ? (
        // Mobile View
        <div className="mobileRoleContainer">
          {roles.map((role, index) => {
            const {
              visible: visibleRoles,
              remaining: roleOverflow,
              remainingData: remainingRoles
            } = truncateItems(role?.role || [], maxVisibleItems);

            const {
              visible: visibleTeams,
              remaining: teamOverflow,
              remainingData: remainingTeams
            } = truncateItems(role?.team || [], maxVisibleItems);

            return (
              <div key={index} className="mobileRoleCard">
                <div className="mobileRoleHeader">
                  {/* <h3 className="mobileRoleName">{role.name}</h3> */}
                  <div className="mobileRoleActions">
                    <img
                      src={editIcon}
                      alt="Edit role"
                      onClick={() => {
                        handleEditRole(role, index);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    <img
                      src={deleteIcon}
                      alt="Delete role"
                      onClick={() => {
                        initiateUserDelete(role);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    <img
                      src={forgetPassIcon}
                      alt="Forget Password"
                      onClick={() => handleChangePassword(index)}
                      style={{ cursor: "pointer" }}
                    />
                    {canBlockUnblock && (
                      <div
                        className="ActionItem"
                        onClick={() => {
                          isUserBlocked(role)
                            ? initiateUnblock(role)
                            : initiateBlock(role);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <GetActionIcon
                          type="block"
                          color={isUserBlocked(role) ? "#DC1818" : (theme === "light" ? "#211D25" : "#FFF")}
                          size={18}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mobileRoleSection">
                  <p>{role.email || "No email provided"}</p>
                </div>

                <div className="mobileRoleSection">
                  <div className="mobileSectionLabel">Teams</div>
                  <div className="mobileRolesContainer">
                    {visibleTeams.map((team, idx) => (
                      <div key={idx} className="mobileRoleItem teamItem">
                        <p>{team}</p>
                      </div>
                    ))}
                    {teamOverflow > 0 && (
                      <Tooltip
                        text={remainingTeams.join(", ")}
                        mockClass={"manageTable"}
                      >
                        <div className="mobileExtraCount">
                          <p>+{teamOverflow}</p>
                        </div>
                      </Tooltip>
                    )}
                    {visibleTeams.length === 0 && (
                      <div className="mobileRoleItem">
                        <p>No teams added</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mobileRoleSection">
                  <div className="mobileSectionLabel">Roles</div>
                  <div className="mobileRolesContainer">
                    {visibleRoles.map((r, idx) => (
                      <div key={idx} className="mobileRoleItem usersItem">
                        <p>{r}</p>
                      </div>
                    ))}
                    {roleOverflow > 0 && (
                      <Tooltip
                        text={remainingRoles.join(", ")}
                        mockClass={"manageTable"}
                      >
                        <div className="mobileRoleCount">
                          <p>+{roleOverflow}</p>
                        </div>
                      </Tooltip>
                    )}
                    {visibleRoles.length === 0 && (
                      <div className="mobileRoleItem">
                        <p>No roles added</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Desktop View
        <table className="ManageUserTableGrid">
          <thead>
            <tr>
              {/* <th className="col-role">Name</th> */}
              <th className="col-users">Email</th>
              <th className="col-teams">Roles</th>
              <th className="col-operations">Teams</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role, index) => {
              const {
                visible: visibleRoles,
              } = truncateItems(role?.role || [], maxVisibleItems);

              const {
                visible: visibleTeams,
                remaining: teamOverflow,
                remainingData: remainingTeams
              } = truncateItems(role?.team || [], maxVisibleItems);

              return (
                <tr key={index}>
                  {/* <td className="col-role RoleName">{role.name}</td> */}
                  <td className="col-users">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{role.email}</span>
                      {/* blocked badge intentionally removed */}
                    </div>
                  </td>

                  <td className="col-teams Teams">
                    <div className="TeamsContainer">
                      <div className="TeamItem">
                        <p>
                          {visibleRoles.length > 0
                            ? (role?.role || []).join(", ")
                            : "No roles added"}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="col-operations Operations">
                    <div className="OperationsContainer">
                      {visibleTeams.map((team, idx) => (
                        <div key={idx} className="OperationItem">
                          <p>{team}</p>
                        </div>
                      ))}
                      {teamOverflow > 0 && (

                        <div className="extraCount">
                          <p>+{teamOverflow}</p>
                        </div>

                      )}
                      {visibleTeams.length === 0 && (
                        <div className="OperationItem">No teams added</div>
                      )}
                    </div>
                  </td>

                  <td className="col-actions Actions">
                    <div className="ActionsContainer" >
                      <span data-tooltip="Edit" className="action-icon-wrap">
                        <img
                          src={editIcon}
                          alt="Edit role"
                          onClick={() => {
                            handleEditRole(role, index);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </span>
                      <span data-tooltip="Trash" className="action-icon-wrap">
                        <img
                          src={deleteIcon}
                          alt="Delete user"
                          onClick={() => initiateUserDelete(role)}
                          style={{ cursor: "pointer" }}
                        />
                      </span>
                      <span data-tooltip="Reset Password" className="action-icon-wrap">
                        <img
                          src={forgetPassIcon}
                          alt="Delete role"
                          onClick={() => {
                            handleChangePassword(index);      // if you want to uncomment this, make sure to add handleDeleteRole in props
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </span>
                      {canBlockUnblock && (
                        <span data-tooltip={isUserBlocked(role) ? "Unblock" : "Block"} className="action-icon-wrap">
                          <div
                            className="ActionItem"
                            onClick={() => {
                              isUserBlocked(role)
                                ? initiateUnblock(role)
                                : initiateBlock(role);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <GetActionIcon
                              type="block"
                              color={isUserBlocked(role) ? "#DC1818" : (theme === "light" ? "#211D25" : "#FFF")}
                              size={20}
                            />
                          </div>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {showBlockConfirm && (
        <DeleteModal
          type="block"
          headerTitle="Block User"
          submitBtnText="Block"
          isDisabled={isBlocking}
          content={`Are you sure you want to block this user ${userToBlock?.email}?`}
          // title={userToBlock?.email}
          handleCancel={() => {
            setShowBlockConfirm(false);
            setUserToBlock(null);
          }}
          handleSave={handleUserBlock}
        />
      )}
      {showUnblockConfirm && (
        <DeleteModal
          type="unblock"
          headerTitle="Unblock User"
          submitBtnText="Unblock"
          isDisabled={isUnblocking}
          content={`Are you sure you want to unblock this user ${userToUnblock?.email}?`}
          // title={userToUnblock?.email}
          handleCancel={() => {
            setShowUnblockConfirm(false);
            setUserToUnblock(null);
          }}
          handleSave={handleUserUnblock}
        />
      )}
      {showDeleteOtpModal && (
        <AuthenticationOtp
          handleCancelModel={() => {
            if (isDeleting) return;
            setShowDeleteOtpModal(false);
            setUserToDelete(null);
          }}
          handleCancel={() => {
            if (isDeleting) return;
            setShowDeleteOtpModal(false);
            setUserToDelete(null);
          }}
          handleSave={handleUserDelete}
          title={`Enter your TOTP to delete ${userToDelete?.email || "user"}`}
        />
      )}
    </div>
  );
}

export default ManageUserTable;
